import { CancelOutlined } from '@mui/icons-material';
import { Box, Button, SxProps, Theme, Typography, alpha } from '@mui/material';
import { ReactNode, useCallback } from 'react';

interface DialogContentProps {
    onClose?: () => void;
    children: ReactNode;
    title?: string;
    buttons?: ReactNode | null;
    contentSx?: SxProps<Theme>;
    titleSx?: SxProps<Theme>;
    scrollable?: boolean;
}

export const DialogBoxContent = ({
    onClose,
    children,
    title = '',
    buttons = null,
    contentSx,
    titleSx,
    scrollable = false,
}: DialogContentProps) => {
    const onCloseDialog = useCallback(() => {
        if (onClose) {
            onClose();
        }
    }, [onClose]);

    const closeButton = onClose ? (
        <Button
            data-testid="btn-close-modal"
            onClick={onCloseDialog}
            sx={{ padding: 0, minWidth: 'auto' }}
        >
            <CancelOutlined
                sx={(theme) => ({
                    color: alpha(theme.palette.common.black, 0.5),
                })}
            />
        </Button>
    ) : null;

    if (scrollable) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center">
                <Box
                    minWidth="440px"
                    bgcolor="background.paper"
                    width="100%"
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        maxHeight: 'calc(100vh - 64px)',
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexShrink: 0,
                            alignItems: 'center',
                            justifyContent: title
                                ? 'space-between'
                                : 'flex-end',
                            paddingTop: '16px',
                            paddingRight: '16px',
                            paddingLeft: '16px',
                            paddingBottom: title ? '8px' : 0,
                            ...titleSx,
                        }}
                    >
                        {title && (
                            <Typography
                                typography="openSans.h2"
                                fontSize="18px"
                                lineHeight="20px"
                                fontWeight="700"
                                paddingBottom="8px"
                            >
                                {title}
                            </Typography>
                        )}
                        {closeButton}
                    </Box>
                    <Box
                        sx={[
                            {
                                flex: 1,
                                minHeight: 0,
                                overflowY: 'auto',
                                paddingRight: '24px',
                                paddingLeft: '24px',
                                paddingBottom: '16px',
                            },
                            ...(Array.isArray(contentSx)
                                ? contentSx
                                : contentSx
                                ? [contentSx]
                                : []),
                        ]}
                    >
                        {children}
                    </Box>
                    {buttons && (
                        <Box sx={{ flexShrink: 0 }}>
                            <Box
                                display="flex"
                                padding="14px 30px"
                                bgcolor="grey.200"
                            >
                                {buttons}
                            </Box>
                        </Box>
                    )}
                </Box>
            </Box>
        );
    }

    return (
        <Box display="flex" justifyContent="center" alignItems="center">
            <Box minWidth="440px" bgcolor="background.paper" width="100%">
                <Box padding="16px" paddingTop={title ? 0 : '16px'}>
                    <Box
                        position="sticky"
                        top={title ? 0 : '16px'}
                        paddingTop={title ? '24px' : 0}
                        display="flex"
                        alignItems="center"
                        justifyContent={title ? 'space-between' : 'flex-end'}
                        marginBottom={title ? '24px' : '0'}
                        sx={titleSx}
                        bgcolor={title ? 'background.paper' : 'transparent'}
                        zIndex={1}
                    >
                        {title && (
                            <Typography
                                typography="openSans.h2"
                                fontSize="18px"
                                lineHeight="20px"
                                fontWeight="700"
                                paddingBottom="8px"
                            >
                                {title}
                            </Typography>
                        )}
                        {closeButton}
                    </Box>
                    <Box padding="0 24px" sx={contentSx}>
                        {children}
                    </Box>
                </Box>
                {buttons && (
                    <Box position="sticky" bottom="0px">
                        <Box
                            display="flex"
                            padding="14px 30px"
                            bgcolor="grey.200"
                        >
                            {buttons}
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    );
};
